import React, { useEffect, useState } from 'react';
import {
    Form,
    Input,
    Select,
    DatePicker,
    InputNumber,
    Button,
    Card,
    Space,
    Table,
    message,
    Typography,
    Row,
    Col,
    Alert,
    Tag,
} from 'antd';
import {
    PlusOutlined,
    MinusCircleOutlined,
    SaveOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { projectAPI, departmentAPI, userAPI } from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ProjectCreatePage = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [contractAmount, setContractAmount] = useState(0);
    const [primaryDepartmentId, setPrimaryDepartmentId] = useState(null);  // ⭐ 추가

    useEffect(() => {
        fetchDepartments();
        fetchUsers();
    }, []);

    const fetchDepartments = async () => {
        try {
            const response = await departmentAPI.getAll({ is_active: 'true' });
            const filteredDepts = (response.data.departments || [])
                .filter(dept => dept.code !== 'UNASSIGNED');
            setDepartments(filteredDepts);
        } catch (error) {
            console.error('부서 목록 조회 실패:', error);
            message.error('부서 목록 조회 실패');
        }
    };

    const fetchUsers = async (departmentId = null) => {
        try {
            const response = await userAPI.getSimple();
            let filteredUsers = response.data || [];
            
            // 대표 매출 귀속 부서가 선택된 경우 해당 부서 인력만 필터링
            if (departmentId) {
                filteredUsers = filteredUsers.filter(user => 
                    user.department_id === departmentId
                );
                console.log(`대표 부서 ${departmentId}에 속한 인력:`, filteredUsers.length, '명');
            }
            
            setUsers(filteredUsers);
        } catch (error) {
            console.error('사용자 목록 조회 실패:', error);
            message.error('사용자 목록 조회 실패');
        }
    };

    // ⭐ 대표 부서 선택 시 - 자동으로 100% 분할 추가 및 인력 필터링
    const handlePrimaryDepartmentChange = (deptId) => {
        setPrimaryDepartmentId(deptId);
        
        // 대표 부서에 속한 인력만 필터링하여 다시 가져오기
        fetchUsers(deptId);
        
        // PM과 기술리더 선택 초기화 (부서가 변경되었으므로)
        form.setFieldsValue({
            pm_id: null,
            tech_leader_id: null
        });

        // 기존 분할이 없으면 대표 부서 100%로 자동 설정
        if (allocations.length === 0) {
            setAllocations([{
                key: Date.now(),
                department_id: deptId,
                allocation_rate: 100,
            }]);
        } else {
            // 기존 분할이 있으면 대표 부서로 교체 (비율 유지)
            const existingAllocation = allocations.find(a => a.department_id === deptId);
            if (!existingAllocation) {
                // 대표 부서가 분할 목록에 없으면 추가하고 다른 부서들 비율 조정
                const newAllocations = allocations.map(item => ({
                    ...item,
                    allocation_rate: 0
                }));
                newAllocations.push({
                    key: Date.now(),
                    department_id: deptId,
                    allocation_rate: 100,
                });
                setAllocations(newAllocations);
            }
        }
    };

    // 부서별 매출 분할 추가
    const addAllocation = () => {
        if (!primaryDepartmentId) {
            message.warning('먼저 대표 매출 귀속 부서를 선택해주세요');
            return;
        }

        // ⭐ 기존 비율들을 균등하게 재분배
        const currentCount = allocations.length;
        const newRate = 100 / (currentCount + 1);

        const updatedAllocations = allocations.map(item => ({
            ...item,
            allocation_rate: newRate
        }));

        updatedAllocations.push({
            key: Date.now(),
            department_id: null,
            allocation_rate: newRate,
        });

        setAllocations(updatedAllocations);
    };

    // 부서별 매출 분할 제거
    const removeAllocation = (key) => {
        const filtered = allocations.filter((item) => item.key !== key);

        // ⭐ 남은 부서가 있으면 비율을 재분배
        if (filtered.length > 0) {
            const newRate = 100 / filtered.length;
            const updated = filtered.map(item => ({
                ...item,
                allocation_rate: newRate
            }));
            setAllocations(updated);
        } else {
            setAllocations([]);
        }
    };

    // 부서 선택 변경
    const handleDepartmentChange = (key, departmentId) => {
        const updated = allocations.map((item) =>
            item.key === key ? { ...item, department_id: departmentId } : item
        );
        setAllocations(updated);
    };

    // 매출 비율 변경
    const handleRateChange = (key, rate) => {
        const updated = allocations.map((item) =>
            item.key === key ? { ...item, allocation_rate: rate } : item
        );
        setAllocations(updated);
    };

    // 총 분할 비율 계산
    const getTotalRate = () => {
        return allocations.reduce((sum, item) => sum + (parseFloat(item.allocation_rate) || 0), 0);
    };

    // 부서별 귀속 금액 계산
    const getAllocatedAmount = (rate) => {
        return (contractAmount * rate) / 100;
    };

    // 폼 제출
    const onFinish = async (values) => {
        // 매출 분할 검증
        if (allocations.length > 0) {
            const totalRate = getTotalRate();
            if (Math.abs(totalRate - 100) > 0.01) {
                message.error(`매출 분할 비율의 합이 100%가 아닙니다 (현재: ${totalRate.toFixed(1)}%)`);
                return;
            }

            const deptIds = allocations.map((a) => a.department_id);
            if (new Set(deptIds).size !== deptIds.length) {
                message.error('같은 부서를 중복으로 선택할 수 없습니다');
                return;
            }

            if (allocations.some((a) => !a.department_id)) {
                message.error('모든 분할 항목의 부서를 선택해주세요');
                return;
            }
        }

        setLoading(true);
        try {
            const projectData = {
                ...values,
                contract_date: values.contract_date?.format('YYYY-MM-DD'),
                start_date: values.start_date?.format('YYYY-MM-DD'),
                end_date: values.end_date?.format('YYYY-MM-DD'),
                department_allocations: allocations.map((item) => ({
                    department_id: item.department_id,
                    allocation_rate: item.allocation_rate,
                })),
            };

            if (!projectData.code || projectData.code.trim() === '') {
                delete projectData.code;
            }

            const response = await projectAPI.create(projectData);
            message.success(`프로젝트가 생성되었습니다 (코드: ${response.data.project.code})`);
            navigate('/projects');
        } catch (error) {
            message.error(error.response?.data?.message || '프로젝트 생성 실패');
            console.error('Create project error:', error);
        } finally {
            setLoading(false);
        }
    };

    // 매출 분할 테이블 컬럼
    const allocationColumns = [
        {
            title: '부서',
            key: 'department',
            render: (_, record) => (
                <Select
                    placeholder="부서 선택"
                    value={record.department_id}
                    onChange={(value) => handleDepartmentChange(record.key, value)}
                    style={{ width: 200 }}
                    showSearch
                    optionFilterProp="label"
                    options={departments.map((dept) => ({
                        label: `${dept.name} (${dept.code})`,
                        value: dept.id,
                    }))}
                />
            ),
        },
        {
            title: '매출 귀속 비율 (%)',
            key: 'rate',
            render: (_, record) => (
                <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    value={record.allocation_rate}
                    onChange={(value) => handleRateChange(record.key, value)}
                    style={{ width: 150 }}
                    addonAfter="%"
                />
            ),
        },
        {
            title: '귀속 금액',
            key: 'amount',
            render: (_, record) => (
                <Text strong>
                    {getAllocatedAmount(record.allocation_rate).toLocaleString()}원
                </Text>
            ),
        },
        {
            title: '작업',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => removeAllocation(record.key)}
                />
            ),
        },
    ];

    const totalRate = getTotalRate();
    const isRateValid = Math.abs(totalRate - 100) < 0.01;

    return (
        <div>
            <Space style={{ marginBottom: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/projects')}>
                    목록으로
                </Button>
            </Space>

            <Title level={2}>새 프로젝트 생성</Title>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                    status: 'planning',
                    progress: 0,
                }}
            >
                {/* 기본 정보 */}
                <Card title="기본 정보" style={{ marginTop: 24 }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="code"
                                label="프로젝트 코드"
                                extra="미입력 시 자동 생성됩니다 (예: PJ-2026-001)"
                                rules={[
                                    {
                                        pattern: /^PJ-\d{4}-\d{3}$/,
                                        message: '프로젝트 코드 형식: PJ-YYYY-XXX (예: PJ-2026-001)'
                                    }
                                ]}
                            >
                                <Input placeholder="예: PJ-2026-001 (자동 생성)" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="name"
                                label="프로젝트명"
                                rules={[{ required: true, message: '프로젝트명을 입력해주세요' }]}
                            >
                                <Input placeholder="프로젝트명" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        {/* ⭐ 사업 구분 → 대표 매출 귀속 부서 */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="primary_department_id"
                                label="대표 매출 귀속 부서"
                                rules={[{ required: true, message: '대표 부서를 선택해주세요' }]}
                                extra="부서별 매출 분할을 하지 않을 경우, 이 부서에 100% 귀속됩니다. PM과 기술리더는 이 부서에 속한 인력 중에서만 선택 가능합니다."
                            >
                                <Select
                                    placeholder="부서 선택"
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={handlePrimaryDepartmentChange}  // ⭐ 추가
                                    options={departments.map((dept) => ({
                                        label: `${dept.name} (${dept.code})`,
                                        value: dept.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="status" label="상태">
                                <Select
                                    options={[
                                        { label: '계획', value: 'planning' },
                                        { label: '진행중', value: 'in_progress' },
                                        { label: '보류', value: 'on_hold' },
                                        { label: '완료', value: 'completed' },
                                        { label: '취소', value: 'cancelled' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="progress" label="진행률 (%)">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="client_name" label="고객사명">
                                <Input placeholder="고객사명" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="client_contact" label="고객사 담당자">
                                <Input placeholder="담당자명" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="location" label="프로젝트 현장 주소">
                                <Input placeholder="주소" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="프로젝트 설명">
                        <TextArea rows={3} placeholder="프로젝트 설명" />
                    </Form.Item>

                    <Form.Item 
                        name="flow_link" 
                        label="공정관리 플로우 링크"
                        rules={[
                            { 
                                type: 'url', 
                                message: '올바른 URL 형식을 입력하세요' 
                            }
                        ]}
                    >
                        <Input 
                            placeholder="https://flow.example.com/project/..." 
                            addonBefore="🔗"
                        />
                    </Form.Item>
                </Card>

                {/* 계약 정보 */}
                <Card title="계약 정보" style={{ marginTop: 24 }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="contract_amount"
                                label="계약금액"
                                rules={[{ required: true, message: '계약금액을 입력해주세요' }]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                    addonAfter="원"
                                    onChange={(value) => setContractAmount(value || 0)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="contract_date" label="계약일">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="start_date" label="시작일">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="end_date" label="종료일">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* 인력 배치 */}
                <Card title="인력 배치" style={{ marginTop: 24 }}>
                    <Alert
                        message="자동 프로젝트 멤버 등록"
                        description="선택된 PM과 기술리더는 프로젝트 생성 시 자동으로 프로젝트 멤버에 등록됩니다."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="pm_id" label="PM" 
                                       rules={[{ required: true, message: 'PM을 선택해주세요' }]}>
                                <Select
                                    placeholder={primaryDepartmentId ? "PM 선택" : "먼저 대표 매출 귀속 부서를 선택해주세요"}
                                    showSearch
                                    disabled={!primaryDepartmentId}
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={users.map((user) => ({
                                        label: `${user.name} (${user.employee_id}) - ${user.department?.name}`,
                                        value: user.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="tech_leader_id" label="기술리더"
                                       rules={[{ required: true, message: '기술리더를 선택해주세요' }]}>
                                <Select
                                    placeholder={primaryDepartmentId ? "기술리더 선택" : "먼저 대표 매출 귀속 부서를 선택해주세요"}
                                    showSearch
                                    disabled={!primaryDepartmentId}
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={users.map((user) => ({
                                        label: `${user.name} (${user.employee_id}) - ${user.department?.name}`,
                                        value: user.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* 부서별 매출 분할 */}
                <Card
                    title={
                        <Space>
                            <span>부서별 매출 분할</span>
                            <Tag color="blue">선택사항</Tag>
                        </Space>
                    }
                    style={{ marginTop: 24 }}
                    extra={
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={addAllocation}
                            disabled={!primaryDepartmentId}  // ⭐ 대표 부서 미선택 시 비활성화
                        >
                            부서 추가
                        </Button>
                    }
                >
                    <Alert
                        message="매출을 여러 부서에 분할할 수 있습니다"
                        description={
                            primaryDepartmentId
                                ? `현재 대표 부서가 100%로 설정되어 있습니다. '부서 추가' 버튼으로 다른 부서를 추가하면 비율을 조정할 수 있습니다.`
                                : '먼저 대표 매출 귀속 부서를 선택해주세요.'
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    {allocations.length > 0 && (
                        <>
                            <Table
                                columns={allocationColumns}
                                dataSource={allocations}
                                pagination={false}
                                footer={() => (
                                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Text strong>합계</Text>
                                        <Space size="large">
                                            <Text strong style={{ color: isRateValid ? '#52c41a' : '#ff4d4f' }}>
                                                {totalRate.toFixed(2)}%
                                            </Text>
                                            <Text strong>{contractAmount.toLocaleString()}원</Text>
                                        </Space>
                                    </Space>
                                )}
                            />

                            {!isRateValid && totalRate > 0 && (
                                <Alert
                                    message="매출 분할 비율 오류"
                                    description={`현재 비율의 합: ${totalRate.toFixed(
                                        1
                                    )}% - 정확히 100%가 되어야 합니다`}
                                    type="error"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            )}

                            {isRateValid && (
                                <Alert
                                    message="매출 분할 비율이 올바릅니다"
                                    type="success"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            )}
                        </>
                    )}
                </Card>

                {/* 제출 버튼 */}
                <Card style={{ marginTop: 24 }}>
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={loading}
                            size="large"
                        >
                            프로젝트 생성
                        </Button>
                        <Button size="large" onClick={() => navigate('/projects')}>
                            취소
                        </Button>
                    </Space>
                </Card>
            </Form>
        </div>
    );
};

export default ProjectCreatePage;